import { Auth0Client, LogoutOptions, PopupLoginOptions, RedirectLoginOptions, createAuth0Client } from '@auth0/auth0-spa-js'
import { Auth0Roles, Auth0User } from 'models/auth'
import { FC, createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react'
import { getInitRoles, getRolesFromUser } from './authRolesCompanion'
import { UserRole } from 'constants/auth'
import { embedSession } from 'utils/embedSession'
import { resetUploadLeases } from 'components/common/FileAPI/uploadLeases'
import { logAnalyticsEvent, trackUser, unTrackUser } from '../analytics/analyticsUtils'
import i18n, { reloadAllResources, transformLanguageToAuth0Value } from 'translations/i18n'

import axios, { AxiosError } from 'axios'
import { Nullable } from 'models/helpers'
import { isEmbedRoute } from 'constants/router'
import history from 'utils/helpers/history'
import { jsonEqual } from 'utils/validators'
import { clearBeckaSession } from 'utils/becka/becka'
import { logger } from 'utils/logging'

/** Type of the AppState returned by Auth0 */
type AppState = Nullable<{
  [x: string]: any
  targetUrl: any
}>

type LoginOptions = RedirectLoginOptions<AppState>

export interface Auth0ContextInterface {
  isAuthenticated: boolean,
  user: Auth0User,
  roles: Auth0Roles,
  loading: boolean,
  popupOpen: boolean,
  refreshUser: () => Promise<Auth0User>,
  loginWithPopup: (options?: PopupLoginOptions) => Promise<void>,
  loginWithRedirect: (options?: LoginOptions) => void,
  handleRedirectCallback?: (appState?: AppState) => void,
  getIdTokenClaims: (...p: any[]) => any,
  getTokenSilently: (...p: any[]) => any,
  getTokenWithPopup: (...p: any[]) => any,
  logout: (...p: any[]) => any,
}

/** A function that routes the user to the right place after login */
const DEFAULT_REDIRECT_CALLBACK = (appState?: AppState) => {
  const path = window.location.pathname
  const url = appState?.targetUrl ?? path
  history.push(url)
}

export const clearAuth0LocalStorage = () => {
  let shouldReload = false
  for (const key in window.localStorage) {
    if (key.includes('auth0')) {
      window.localStorage.removeItem(key)
      shouldReload = true
    }
  }
  if (shouldReload) {
    window.location.reload()
  }
}

const defaultContext: Auth0ContextInterface = {
  isAuthenticated: false,
  user: null,
  roles: getInitRoles(),
  loading: false,
  popupOpen: false,
  refreshUser: async () => null,
  loginWithPopup: async () => { },
  loginWithRedirect: (...p: any[]) => { },
  handleRedirectCallback: DEFAULT_REDIRECT_CALLBACK,
  getIdTokenClaims: (...p: any[]) => null,
  getTokenSilently: (...p: any[]) => null,
  getTokenWithPopup: (...p: any[]) => null,
  logout: (...p: any[]) => null,
}

export let auth0ClientModule: Auth0Client
export const Auth0Context = createContext(defaultContext)
/** Synthetic roles for the Propstack embed: the broker session is always a CLIENT session. */
const embedClientRoles = (): Auth0Roles => ({
  all: new Set([UserRole.CLIENT]),
  isAdmin: false,
  isCreative: false,
  isClient: true,
})

/**
 * Propstack embed (ISSUE-2338): the broker session replaces the Auth0 SPA session entirely, so the
 * auth context must report it as an authenticated CLIENT session — root-level data providers
 * (UserDataContext, appMeta) gate every query on `isAuthenticated` and mount before the in-iframe
 * exchange resolves, hence the reactive subscription.
 */
export const useAuth0 = () => {
  const context = useContext(Auth0Context)
  const embedActive = useSyncExternalStore(embedSession.subscribe, embedSession.isActive)

  return useMemo(() => {
    if (!embedActive) return context
    return {
      ...context,
      isAuthenticated: true,
      loading: false,
      roles: embedClientRoles(),
    }
  }, [context, embedActive])
}
export const Auth0Provider: FC<{
  [x: string]: any
  onRedirectCallback?: ((appState?: AppState) => void)
}> = ({
  children,
  onRedirectCallback,
  ...initOptions
}) => {
    const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false)
    const [user, setUser] = useState<Auth0User>()
    const [auth0Client, setAuth0] = useState<Auth0Client>()
    const [loading, setLoading] = useState(true)
    const [popupOpen, setPopupOpen] = useState(false)

    const roles = useMemo(() => getRolesFromUser(user), [user])

    const onRedirectCallbackMemoized = useCallback((appState?: AppState) => {
      if (onRedirectCallback) return onRedirectCallback(appState)
      const params = appState?.preservedParams ?? ''
      const path = window.location.pathname
      const url = appState?.targetUrl ?? path

      window.location.replace(`${url}${params}`)
    }, [onRedirectCallback])

    const initOptionsRef = useRef(initOptions)
    const onRedirectCallbackRef = useRef(onRedirectCallbackMemoized)

    // Embed surfaces authenticate only through the broker session (ISSUE-2338) — no SPA client here (ISSUE-3180).
    useEffect(() => {
      if (isEmbedRoute()) {
        setLoading(false)
        return
      }
      const initAuth0 = async () => {
        try {
          const auth0FromHook = await createAuth0Client({
            ...initOptionsRef.current,
            domain: initOptionsRef.current.domain,
            clientId: initOptionsRef.current.clientId,
          })

          setAuth0(auth0FromHook)
          auth0ClientModule = auth0FromHook

          try {
            if (window.location.search.includes('code=')) {
              const { appState } = await auth0FromHook.handleRedirectCallback()
              onRedirectCallbackRef.current(appState)
            }
          } catch (e) {
            console.error(e)
            onRedirectCallbackRef.current(null)
          }

          const receivedIsAuthenticated = await auth0FromHook.isAuthenticated()
          setIsAuthenticated(receivedIsAuthenticated)

          if (receivedIsAuthenticated) {
            const receivedUser = await auth0FromHook.getUser<NonNullable<Auth0User>>()
            setUser(receivedUser)
            logAnalyticsEvent('login', { method: 'initAuth0' })
            trackUser(receivedUser)
            reloadAllResources()
          }

          setLoading(false)
        } catch (e) {
          logger.error('Auth0 init failed', e, {
            context: {
              component: 'Auth0Provider',
              action: 'initAuth0'
            }
          })
          clearAuth0LocalStorage()
          setLoading(false)
        }
      }
      initAuth0()
    }, [])

    // Latest auth state for the session-expiry interceptor below, which is registered
    // once per client and must not re-register on every auth state change.
    const isAuthenticatedRef = useRef(isAuthenticated)
    useEffect(() => {
      isAuthenticatedRef.current = isAuthenticated
    }, [isAuthenticated])

    // Session-expiry watchdog (ISSUE-2354): since the auth0-spa-js v2 migration the SDK clears
    // its own localStorage cache on login_required BEFORE our clearAuth0LocalStorage() runs, so
    // the legacy "clear storage + reload" recovery never fires — the app keeps a stale in-memory
    // isAuthenticated=true and every request 401s behind an endless loading state. A 401 arriving
    // while the app still believes it is authenticated is probed with getTokenSilently(): success
    // means the 401 is application-level (e.g. /user/me during signup) and propagates to its
    // caller; failure means the Auth0 session is gone and the user is sent to the login page.
    useEffect(() => {
      if (!auth0Client) return

      let redirecting = false

      const interceptorId = axios.interceptors.response.use(undefined, async (error: AxiosError) => {
        if (error?.response?.status === 401 && isAuthenticatedRef.current && !redirecting) {
          try {
            await auth0Client.getTokenSilently()
          } catch (e) {
            if (!redirecting) {
              redirecting = true

              logger.info('Auth0 session expired, redirecting to login', e, {
                context: { component: 'Auth0Provider', action: 'sessionExpiredLoginRedirect' }
              })

              // End the previous user's Salesforce messaging session, whether or not the chat has loaded
              clearBeckaSession()

              auth0Client.loginWithRedirect({
                appState: {
                  targetUrl: window.location.pathname,
                  preservedParams: window.location.search
                },
                authorizationParams: {
                  mode: 'login',
                  language: transformLanguageToAuth0Value(i18n.language)
                }
              })
            }
          }
        }

        return Promise.reject(error)
      })

      return () => axios.interceptors.response.eject(interceptorId)
    }, [auth0Client])

    const refreshUser = async () => {
      try {
        if (!auth0Client) return
        await auth0Client.checkSession({ cacheMode: 'off' })
        await auth0Client.getTokenSilently({ cacheMode: 'off' })

        const receivedIsAuthenticated = await auth0Client.isAuthenticated()
        if (isAuthenticated !== receivedIsAuthenticated) setIsAuthenticated(receivedIsAuthenticated)

        let receivedUser: Auth0User = null

        if (receivedIsAuthenticated) {
          receivedUser = await auth0Client.getUser<NonNullable<Auth0User>>()
          if (!jsonEqual(receivedUser, user)) {
            setUser(receivedUser)
            logAnalyticsEvent('login', { method: 'refreshUser' })
            trackUser(receivedUser)
            reloadAllResources()
          }
        } else {
          if (!jsonEqual(null, user)) {
            setUser(null)
          }
        }

        return receivedUser
      } catch (e) {
        clearAuth0LocalStorage()
      }
    }

    const loginWithPopup = async (options?: PopupLoginOptions) => {
      if (!auth0Client) return
      setPopupOpen(true)

      // End the previous user's Salesforce messaging session, whether or not the chat has loaded
      clearBeckaSession()

      let popupError: unknown = null
      try {
        await auth0Client.loginWithPopup(options)
      } catch (error) {
        popupError = error
        logger.error('loginWithPopup failed', error, {
          context: {
            component: 'Auth0Provider',
            action: 'loginWithPopup',
            metadata: { errorName: (error as Error)?.name }
          }
        })
      } finally {
        setPopupOpen(false)
      }
      if (popupError) return
      const user = await auth0Client.getUser<NonNullable<Auth0User>>()
      setUser(user)
      logAnalyticsEvent('login', { method: 'loginWithPopup' })
      trackUser(user)
      reloadAllResources()
      if (user) setIsAuthenticated(true)
    }

    const loginWithRedirect = (options: LoginOptions = {
      appState: {
        targetUrl: window.location.pathname,
      }
    }) => {
      if (!auth0Client) return

      // End the previous user's Salesforce messaging session, whether or not the chat has loaded
      clearBeckaSession()

      auth0Client.loginWithRedirect({
        ...options,
        appState: {
          ...options.appState,
          preservedParams: window.location.search,
        }
      })
    }

    return (
      <Auth0Context.Provider
        value={{
          isAuthenticated,
          user,
          roles,
          loading,
          popupOpen,
          refreshUser,
          loginWithPopup,
          loginWithRedirect,
          getIdTokenClaims: () => auth0Client?.getIdTokenClaims(),
          getTokenSilently: (...p: any[]) => auth0Client?.getTokenSilently(...p),
          getTokenWithPopup: (...p: any[]) => auth0Client?.getTokenWithPopup(...p),
          logout: (options: LogoutOptions = {
            logoutParams: { returnTo: window.location.origin }
          }) => {
            unTrackUser()
            // The upload-lease heartbeat runs on the authenticated client and lives in a module singleton,
            // so a lease left behind by an upload that never settled would keep posting with a dead token.
            resetUploadLeases()
            // Logout returns to the same origin in the same tab, so sessionStorage survives it and the next
            // user to sign in here would inherit this one's selected workspace. It outranks the workspace
            // preferred server-side, so leaving it behind lets stale local state override a deliberate
            // choice. Access is still validated on resolution, so this is a wrong-workspace risk, not an
            // access one.
            sessionStorage.removeItem('workspaceId')
            return auth0Client?.logout(options)
          },
        }}
      >
        {children}
      </Auth0Context.Provider>
    )
  }
